import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { flatten, isNil } from 'lodash-es';
import { parse } from 'query-string';
import dayjs from 'dayjs';

import { DeliveryLogType, StatusMessageCategory } from '@dev/ee-api/ee-api-models';

import { EventLogParamsByChannelName } from '@/store/reports/types';
import { ApplicationState } from '@/store';
import { LONG_DATE_FORMAT } from '@/services/constants';
import { useTimeRange } from './useTimeRange';

export const useRequestParams = (channelName: string, isLastEvent?: boolean): EventLogParamsByChannelName => {
    const {
        reports: { eventLogParamsByChannelName },
    } = useSelector((state: ApplicationState) => state);
    const location = useLocation();

    const query = parse(location.search, { arrayFormat: 'comma', parseBooleans: true });
    const statuses = isNil(query.events) ? [DeliveryLogType.All] : (flatten([query.events]) as DeliveryLogType[]);
    const messageCategory = isNil(query.result) ? null : (flatten([query.result]) as StatusMessageCategory[]);
    const limit = query?.perPage ? +query.perPage : eventLogParamsByChannelName?.limit;
    const urlOffset = query?.page ? +query.page * limit - limit : 0;
    const offset = urlOffset || eventLogParamsByChannelName?.offset;
    const timeRange = useTimeRange();
    const from = query?.from
        ? dayjs(query?.from as string)
              .startOf('day')
              .utc()
              .format(LONG_DATE_FORMAT)
        : dayjs().startOf('day').utc().subtract(timeRange, 'day').format(LONG_DATE_FORMAT);
    const to = query?.to
        ? dayjs(query?.to as string)
              .endOf('day')
              .utc()
              .format(LONG_DATE_FORMAT)
        : dayjs().endOf('day').utc().format(LONG_DATE_FORMAT);
    const email = query?.search ? (query?.search as string) : null;
    const lastEvent = isLastEvent || query.lastEvent === 'true';
    return {
        channelName,
        from,
        to,
        lastEvent,
        statuses,
        messageCategory,
        offset,
        limit,
        email,
    };
};
